"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { faChevronLeft, faChevronRight, FontAwesomeIcon } from '@/constants/icons';

interface Props {
    children: React.ReactNode;
}

const OfferSlider = ({ children }: Props) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const slides = React.Children.toArray(children);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        setIsMobile(mq.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const showMobileButtons = isMobile && slides.length > 1;
    const showDesktopButtons = !isMobile && slides.length > 4;
    const showButtons = showMobileButtons || showDesktopButtons;

    const getScrollStep = useCallback(() => {
        const track = trackRef.current;
        if (!track) return 0;
        const firstCard = track.children[0] as HTMLElement | undefined;
        if (!firstCard) return track.clientWidth / 4;
        const gap = parseInt(getComputedStyle(track).gap) || 16;
        return firstCard.offsetWidth + gap;
    }, []);

    const scrollByAmount = useCallback((direction: 'prev' | 'next') => {
        const track = trackRef.current;
        if (!track) return;
        const step = getScrollStep();
        if (direction === 'next') {
            const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
            track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + step, behavior: 'smooth' });
        } else {
            const atStart = track.scrollLeft <= 4;
            track.scrollTo({ left: atStart ? track.scrollWidth : track.scrollLeft - step, behavior: 'smooth' });
        }
    }, [getScrollStep]);

    const prev = useCallback(() => scrollByAmount('prev'), [scrollByAmount]);
    const next = useCallback(() => scrollByAmount('next'), [scrollByAmount]);

    useEffect(() => {
        if (!showButtons) return;
        if (isHovered) {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current);
            return;
        }
        autoPlayRef.current = setInterval(() => {
            const track = trackRef.current;
            if (!track) return;
            const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
            track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + getScrollStep(), behavior: 'smooth' });
        }, 4000);
        return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
    }, [isHovered, showButtons, getScrollStep]);

    // Button shared classes
    const btnBase = "absolute top-1/2 -translate-y-1/2 z-20 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-500 hover:text-white hover:bg-[#D61C4E] hover:border-[#D61C4E] transition-all duration-300";

    return (
        <div
            className="relative w-full"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* ── Prev button ── */}
            {showButtons && (
                <button
                    onClick={prev}
                    aria-label="Previous"
                    className={`${btnBase} ${
                        isMobile
                            ? 'left-0 w-8 h-8'      // mobile: flush left edge of wrapper
                            : '-left-5 w-10 h-10'   // desktop: outside track
                    }`}
                >
                    <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
                </button>
            )}

            {/* ── Next button ── */}
            {showButtons && (
                <button
                    onClick={next}
                    aria-label="Next"
                    className={`${btnBase} ${
                        isMobile
                            ? 'right-0 w-8 h-8'     // mobile: flush right edge of wrapper
                            : '-right-5 w-10 h-10'  // desktop: outside track
                    }`}
                >
                    <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                </button>
            )}

            {/* ── Track — mobile: indent to clear buttons; desktop: no indent ── */}
            <div
                ref={trackRef}
                className={`flex gap-4 overflow-x-auto pb-2 pt-1
                    ${!showButtons ? 'justify-center' : ''}
                    ${showMobileButtons ? 'mx-10' : ''}
                `}
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    scrollSnapType: showButtons ? 'x mandatory' : 'none',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {slides.map((child, i) => (
                    <div
                        key={i}
                        className="flex-shrink-0 w-full sm:w-[46%] md:w-[31%] lg:w-[calc(25%-12px)]"
                        style={{ scrollSnapAlign: showButtons ? 'start' : 'none' }}
                    >
                        {child}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OfferSlider;
